import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  withStyles,
  createStyles,
  Typography,
  Box,
  makeStyles,
  Tooltip,
  TableRow
} from "@material-ui/core";
import { InternalValuationReviewStore } from "../stores/internalValuationReviewStore";
import { getStringOrDefault } from "../utils/utils";
import { createNumberFormatField, createRadioField, createSelectField, createTextField, createPercentageField, createNumberFormatFieldWithAdornment, createDateField, createCurrencyField, createNumberFormatFieldNoDecimalsAndWithLimit, createCheckboxField } from "@roc/ui";
import { PropertyCompFormStore } from "../stores/propertyCompFormStore";
import { observer } from "mobx-react";
import { comparabilityOptions, propertyTypeOptions, viewOptions } from "../utils/constants";
import { PropertyInformationFormStore } from "../stores/propertyInformationFormStore";
import { PropertyImage } from "./propertyImage";


const useStyles = makeStyles(() => ({
  fontSize: {
    fontSize: "0.9rem"
  },
  radioContainer: {
    "& .MuiFormControl-fullWidth": {
      width: "auto !important"
    }
  },
  fieldContainer: {
    "& input": {
      backgroundColor: "#FFFFFF !important",
      borderRadius: "12px"
    },
    "& .MuiOutlinedInput-multiline": {//for textarea
      backgroundColor: "#FFFFFF !important",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF !important",
      borderRadius: "8px"
    },
    "& .MuiFormControlLabel-root": {
      margin: "0 !important"
    }
  },
  subjectPropertyCell: {
    fontSize: "0.9rem",
    backgroundColor: "#B8F7FA"
  },
  subjectPropertyTableContainer: {
    position: "relative",
    zIndex: 2,
    boxShadow: "18px 0 15px -11px rgba(0, 0, 0, 0.2)",
  },
  compsTableContainer: {
    position: "relative",
    zIndex: 1,
    overflowX: "auto",
  },
  mlsComments: {
    maxHeight: 150,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 6,
    WebkitBoxOrient: 'vertical',
    lineHeight: '1.1rem'
  },
  tooltip: {
    fontSize: '1rem',
  },
  imgContainer: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minHeight: "100px",
  }
}));

const StyledTableRow = withStyles({
  root: {
    height: 80,
  },
})(TableRow);

const StyledTableCell = withStyles(() =>
  createStyles({
    body: {
      padding: 10,
      border: "1px solid black",
      textAlign: "left",
      width: "300px"
    },
    head: {
      fontWeight: "bold",
      border: "1px solid black",
      textAlign: "center",
      padding: 10,
      letterSpacing: "1px",
      width: "300px"
    },
  })
)(TableCell);

const StyledTableCellNoBorders = withStyles(() =>
  createStyles({
    body: {
      padding: 10,
      borderBottom: "none",
      width: "300px"
    },
  })
)(TableCell);


interface ComparablesTableProps {
  internalValuationReviewStore: InternalValuationReviewStore;
}

export const ComparablesTable = observer(({ internalValuationReviewStore }: ComparablesTableProps) => {
  const classes = useStyles();

  const compForms = internalValuationReviewStore.propertyCompFormStores.map(store => store.getFlattenedValues());

  if (compForms.length === 0) {
    return (<Typography variant="h6">
      Comparable Properties Not Available
    </Typography>)
  }

  const readOnly = internalValuationReviewStore.readOnly;

  const [subjectPropertyCompStore, ...propertyCompsStores] = internalValuationReviewStore.propertyCompFormStores;
  const [subjectPropertyCompForm, ...propertyCompsForms] = compForms;

  const { propertyInformationFormStore } = internalValuationReviewStore.subjectPropertyInformationStore;

  return (

    <Box display="flex" width="100%">
      <Box className={classes.subjectPropertyTableContainer}>
        <TableContainer>
          <Table style={{ tableLayout: "fixed", width: '0px' }}>
            <TableHead>
              <StyledTableRow key="header-row">
                <StyledTableCell></StyledTableCell>
                <StyledTableCell key={`header-cell-subject-property}`} className={classes.subjectPropertyCell} >{"SUBJECT PROPERTY"}</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {rows.map((field) => {
                const isMlsCommentsCell = field === MLS_COMMENTS;
                const isPropertyImageCell = field === PROPERTY_IMAGE;

                return (
                  <StyledTableRow key={`body-row-${field}`}>
                    <StyledTableCell key={`body-row-cell-${field}`} className={classes.fontSize} style={{ height: isMlsCommentsCell ? "150px" : "90px" }}>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        {isPropertyImageCell ? '' : field}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell key={`body-row-cell-${field}-subject-property`} className={classes.subjectPropertyCell} style={{ height: isMlsCommentsCell ? "150px" : "90px" }}>
                      <Box className={classes.fieldContainer}>
                        <TableInputField
                          field={field}
                          property={subjectPropertyCompForm}
                          propertyCompFormStore={subjectPropertyCompStore}
                          propertyInformationFormStore={propertyInformationFormStore}
                          readOnly={readOnly} />
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                )
              })}
              {/* Best Comparable Section */}
              {<StyledTableRow>
                <StyledTableCellNoBorders className={classes.fontSize}>
                  <Box display="flex" justifyContent="center" alignItems="center">
                    BEST COMPARABLE
                  </Box>
                </StyledTableCellNoBorders>
                <StyledTableCellNoBorders key={`best-comparable-cell-subject-property`} className={classes.subjectPropertyCell} align="center">
                </StyledTableCellNoBorders>

              </StyledTableRow>
              }
              {/* Input Fields */}
              {fields.map(
                (field, index) => (
                  <StyledTableRow key={`field-cell${index}`}>
                    <StyledTableCellNoBorders className={classes.fontSize}>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        {field}
                      </Box>
                    </StyledTableCellNoBorders>
                    <StyledTableCellNoBorders key={`field-cell-input-subject-property`} className={classes.subjectPropertyCell}>
                      <Box className={classes.fieldContainer}>
                        <TableInputField
                          field={field}
                          property={subjectPropertyCompForm}
                          propertyCompFormStore={subjectPropertyCompStore}
                          readOnly={readOnly}
                          propertyInformationFormStore={propertyInformationFormStore} />
                      </Box>
                    </StyledTableCellNoBorders>
                  </StyledTableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box flexGrow={1} className={classes.compsTableContainer}>
        <TableContainer >
          <Table style={{ tableLayout: "fixed", width: "0" }}>
            <TableHead>
              <StyledTableRow key="header-row-comps">
                {propertyCompsStores.map((_, i) => (<StyledTableCell key={`header-cell-${i}`} className={classes.fontSize} >{`COMP #${i + 1}`}</StyledTableCell>))}
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {rows.map((field) => {
                const isMlsCommentsCell = field === MLS_COMMENTS;

                return (
                  <StyledTableRow key={`body-row-${field}`} style={{ height: isMlsCommentsCell ? "150px" : "90px" }}>
                    {propertyCompsForms.map((row, i) => (
                      <StyledTableCell key={`body-row-cell-${field}-${i}`} className={classes.fontSize} style={{ height: isMlsCommentsCell ? "150px" : "90px" }}>
                        <Box className={classes.fieldContainer}>
                          <TableInputField
                            field={field}
                            property={row}
                            propertyCompFormStore={propertyCompsStores[i]}
                            propertyInformationFormStore={propertyInformationFormStore}
                            readOnly={readOnly} />
                        </Box>
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                );
              })}
              {/* Best Comparable Section */}
              {<StyledTableRow>
                {propertyCompsForms.map((comp: any, index) => {
                  const propertyCompFormStore = internalValuationReviewStore.propertyCompFormStores[index + 1];
                  return (
                    <StyledTableCellNoBorders key={`best-comparable-cell-${index}`} align="center">
                      <Box className={classes.radioContainer} >
                        {createRadioField({
                          store: propertyCompFormStore,
                          testId: 'bestComparable' + propertyCompFormStore.form.fields.comparePropertyId.value,
                          fieldName: 'bestComparable',
                          label: '',
                          options: [{ label: "Best Comparable", value: true }],
                          row: true,
                          disabled: readOnly,
                          onChange: (value) => internalValuationReviewStore.onBestComparableChange(propertyCompFormStore.form.fields.propertyCompId.value, value),
                        })}
                      </Box>
                    </StyledTableCellNoBorders>
                  )
                })}
              </StyledTableRow>
              }
              {/* Input Fields */}
              {fields.map(
                (field, index) => (
                  <StyledTableRow key={`field-cell${index}`}>
                    {propertyCompsForms.map((row, colIndex) => (
                      <StyledTableCellNoBorders key={`field-cell-input-${index}-${colIndex}`}>
                        <Box className={classes.fieldContainer}>
                          <TableInputField
                            field={field}
                            property={row}
                            propertyCompFormStore={propertyCompsStores[colIndex]}
                            propertyInformationFormStore={propertyInformationFormStore}
                            readOnly={readOnly} />
                        </Box>
                      </StyledTableCellNoBorders>
                    ))}
                  </StyledTableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
});



const ADDRESS = "ADDRESS";
const CITY_STATE_ZIP = "CITY, STATE ZIP";
const PROPERTY_IMAGE = "PROPERTY IMAGE";
const MLS_NUMBER = "MLS #";
const PROXIMITY = "PROXIMITY";
const ORIGINAL_LIST_DATE = "ORIGINAL LIST DATE";
const ORIGINAL_LIST_PRICE = "ORIGINAL LIST PRICE";
const SALE_DATE = "SALE DATE";
const SALE_PRICE = "SALE PRICE";
const DOM = "DOM";
const PROPERTY_TYPE = "PROPERTY TYPE";
const SALE_TYPE = "SALE TYPE";
const LOT_SIZE = "LOT SIZE";
const YEAR_BUILT = "YEAR BUILT";
const BEDROOMS = "BEDROOMS";
const BATHROOMS = "BATHROOMS";
const PRICE_PER_SQ = "PRICE PER SQ";
const POOL = "POOL";
const MLS_COMMENTS = "MLS COMMENTS";

const rows = [
  PROPERTY_IMAGE,
  ADDRESS,
  CITY_STATE_ZIP,
  MLS_NUMBER,
  PROXIMITY,
  ORIGINAL_LIST_DATE,
  ORIGINAL_LIST_PRICE,
  SALE_DATE,
  SALE_PRICE,
  DOM,
  PROPERTY_TYPE,
  SALE_TYPE,
  LOT_SIZE,
  YEAR_BUILT,
  BEDROOMS,
  BATHROOMS,
  PRICE_PER_SQ,
  POOL,
  MLS_COMMENTS,
];

const WEIGHT_ADJUSTMENT = "WEIGHT ADJUSTMENT";
const NO_OF_UNITS = "NO. OF UNITS";
const PARKING_TYPE = "PARKING TYPE";
const AMENITIES = "AMENITIES";
const VIEW = "VIEW";
const COMPARABILITY = "COMPARABILITY";
const ANALYST_COMMENTS = "ANALYST COMMENTS";

const fields = [NO_OF_UNITS, WEIGHT_ADJUSTMENT, PARKING_TYPE, AMENITIES, VIEW, COMPARABILITY, ANALYST_COMMENTS];


interface TableInputFieldProps {
  propertyCompFormStore: PropertyCompFormStore;
  propertyInformationFormStore: PropertyInformationFormStore;
  property: any;
  field: string;
  readOnly: boolean;
}


function TableInputField({ field, property, propertyCompFormStore, readOnly, propertyInformationFormStore }: TableInputFieldProps) {

  const classes = useStyles();
  const isSubjectProperty = propertyCompFormStore.form.fields.sequence.value === 0;

  switch (field) {
    case ADDRESS:
      return (
        <Tooltip title={getStringOrDefault(property.address) || ""} placement="top" arrow classes={{ tooltip: classes.tooltip }}>
          <Box display="flex" justifyContent="center" alignItems="center">
            {getStringOrDefault(property.address)}
          </Box>
        </Tooltip>
      );
    case CITY_STATE_ZIP: {
      const value = `${property.city}, ${property.state} ${property.zipCode}`;
      return (
        <Tooltip title={value || ""} placement="top" arrow classes={{ tooltip: classes.tooltip }}>
          <Box display="flex" justifyContent="center" alignItems="center">{value}</Box>
        </Tooltip>
      );
    }
    case PROPERTY_IMAGE: {
      return (
        <PropertyImage property={property} />
      );
    }
    case MLS_NUMBER:
      return createTextField({
        label: '',
        fieldName: 'mlsNumber',
        testId: 'mlsNumber-' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        disabled: readOnly
      });
    case PROXIMITY: {
      if (isSubjectProperty) {
        return (<Box display="flex" justifyContent="center" alignItems="center"></Box>);
      } else {
        return createNumberFormatFieldWithAdornment({
          label: '',
          fieldName: 'proximity',
          testId: 'proximity-' + property.property_id,
          store: propertyCompFormStore,
          standaloneLabel: false,
          adornment: "mi",
          adornmentPosition: "end",
          disabled: readOnly
        });
      }
    }
    case ORIGINAL_LIST_DATE:
      return createDateField({
        label: '',
        fieldName: 'originalListDate',
        testId: 'originalListDate-' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        disabled: readOnly
      });
    case ORIGINAL_LIST_PRICE:
      return createCurrencyField({
        label: '',
        fieldName: 'originalListPrice',
        testId: 'originalListPrice' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        disabled: readOnly
      });
    case SALE_DATE:
      return createDateField({
        label: '',
        fieldName: 'saleDate',
        testId: 'saleDate-' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        disabled: readOnly
      });
    case SALE_PRICE:
      return createCurrencyField({
        label: '',
        fieldName: 'salePrice',
        testId: 'salePrice' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        disabled: readOnly
      });
    case DOM:
      return createNumberFormatFieldNoDecimalsAndWithLimit({
        label: '',
        fieldName: 'domTrend',
        testId: 'domTrend' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        allowNegative: false,
        disabled: readOnly
      });
    case PROPERTY_TYPE:
      return createSelectField({
        store: propertyCompFormStore,
        testId: 'propertyType-' + property.property_id,
        fieldName: 'propertyType',
        label: '',
        options: propertyTypeOptions,
        standaloneLabel: true,
        onChange: (value) => {
          propertyCompFormStore.onFieldChange('propertyType', value);
          if (property.sequence === 0) {
            propertyInformationFormStore.onFieldChange('propertyType', value);
          }
        },
        disabled: readOnly
      });
    case SALE_TYPE:
      return createTextField({
        label: '',
        fieldName: 'saleType',
        testId: 'saleType-' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        disabled: readOnly
      });
    case LOT_SIZE:
      return createNumberFormatField({
        label: '',
        fieldName: 'lotSize',
        testId: 'lotSize-' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        allowNegative: false,
        disabled: readOnly
      });
    case YEAR_BUILT:
      return createNumberFormatFieldNoDecimalsAndWithLimit({
        label: '',
        fieldName: 'yearBuilt',
        testId: 'yearBuilt-' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        allowNegative: false,
        onChange: (value) => {
          propertyCompFormStore.onFieldChange('yearBuilt', value);
          if (property.sequence === 0) {
            propertyInformationFormStore.onFieldChange('yearBuilt', value);
          }
        },
        disabled: readOnly
      });
    case BEDROOMS:
      return createNumberFormatFieldNoDecimalsAndWithLimit({
        label: '',
        fieldName: 'beds',
        testId: 'beds-' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        allowNegative: false,
        onChange: (value) => {
          propertyCompFormStore.onFieldChange('beds', value);
          if (property.sequence === 0) {
            propertyInformationFormStore.onFieldChange('beds', value);
          }
        },
        disabled: readOnly
      });
    case BATHROOMS:
      return createNumberFormatFieldNoDecimalsAndWithLimit({
        label: '',
        fieldName: 'baths',
        testId: 'baths-' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        allowNegative: false,
        onChange: (value) => {
          propertyCompFormStore.onFieldChange('baths', value);
          if (property.sequence === 0) {
            propertyInformationFormStore.onFieldChange('baths', value);
          }
        },
        disabled: readOnly
      });
    case PRICE_PER_SQ:
      return createCurrencyField({
        label: '',
        fieldName: 'pricePerSqFt',
        testId: 'pricePerSqFt' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        disabled: readOnly
      });
    case POOL:
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          {createCheckboxField({
            label: '',
            fieldName: 'hasPool',
            store: propertyCompFormStore,
            disabled: readOnly,
            color: "primary"
          })}
        </Box>);
    case MLS_COMMENTS: {
      return createTextField({
        label: '',
        fieldName: 'mlsComments',
        testId: 'mlsComments-' + property.property_id,
        store: propertyCompFormStore,
        standaloneLabel: false,
        multiline: true,
        minRows: 4,
        InputProps: {
          inputProps: {
            style: {
              maxHeight: "90px",
              overflow: "auto"
            }
          }
        },
        disabled: readOnly
      });
    }
    case NO_OF_UNITS:
      return createNumberFormatField({
        label: '',
        fieldName: 'noOfUnits',
        testId: 'noOfUnits' + propertyCompFormStore.form.fields.comparePropertyId.value,
        store: propertyCompFormStore,
        standaloneLabel: false,
        disabled: readOnly
      });
    case WEIGHT_ADJUSTMENT:
      if (isSubjectProperty) {
        return (<></>)
      } else {
        return createPercentageField({
          label: '',
          fieldName: 'weightAdjustment',
          testId: 'weightAdjustment' + propertyCompFormStore.form.fields.comparePropertyId.value,
          store: propertyCompFormStore,
          standaloneLabel: false,
          disabled: readOnly
        });
      }
    case PARKING_TYPE:
      return createTextField({
        label: '',
        fieldName: 'parkingType',
        testId: 'parkingType' + propertyCompFormStore.form.fields.comparePropertyId.value,
        store: propertyCompFormStore,
        standaloneLabel: false,
        disabled: readOnly
      });
    case AMENITIES:
      return createTextField({
        label: '',
        fieldName: 'amenities',
        testId: 'amenities' + propertyCompFormStore.form.fields.comparePropertyId.value,
        store: propertyCompFormStore,
        standaloneLabel: false,
        disabled: readOnly
      });
    case VIEW:
      if (isSubjectProperty) {
        return createTextField({
          label: '',
          fieldName: 'view',
          testId: 'view' + propertyCompFormStore.form.fields.comparePropertyId.value,
          store: propertyCompFormStore,
          standaloneLabel: false,
          disabled: readOnly
        });
      } else {
        return createSelectField({
          store: propertyCompFormStore,
          testId: 'view',
          fieldName: 'view',
          label: '',
          options: viewOptions,
          standaloneLabel: true,
          disabled: readOnly
        });
      }
    case COMPARABILITY:
      if (isSubjectProperty) {
        return createTextField({
          label: '',
          fieldName: 'comparability',
          testId: 'comparability' + propertyCompFormStore.form.fields.comparePropertyId.value,
          store: propertyCompFormStore,
          standaloneLabel: false,
          disabled: readOnly
        });
      }
      return createSelectField({
        label: '',
        fieldName: 'comparability',
        testId: 'comparability' + propertyCompFormStore.form.fields.comparePropertyId.value,
        store: propertyCompFormStore,
        options: comparabilityOptions,
        standaloneLabel: true,
        disabled: readOnly
      });
    case ANALYST_COMMENTS:
      return createTextField({
        label: '',
        fieldName: 'analystComments',
        testId: 'analystComments' + propertyCompFormStore.form.fields.comparePropertyId.value,
        store: propertyCompFormStore,
        standaloneLabel: false,
        multiline: true,
        minRows: 4,
        disabled: readOnly
      });
    default:
      return null;
  }
}